import React, { ReactNode, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import {
  Provider as ReduxProvider,
  useDispatch,
  useSelector,
} from 'react-redux';
import { RouterProvider } from 'react-router-dom';

import { StyleProvider, px2remTransformer } from '@ant-design/cssinjs';
import { injectStore } from '@api/api';
import { LanguageKey, LanguageMap } from '@models/common';
import { IRootState } from '@store/reducers';
import { setLanguage } from '@store/reducers/preferencesSlice';
import {
  SeedToken,
  MapToken,
  AliasToken,
  CustomToken,
  Component,
} from '@style/theme';
import setPrefixForLocalStorage from '@utils/setPrefixForLocalStorage';
import { App, ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import 'dayjs/locale/zh-tw';
import duration from 'dayjs/plugin/duration';

import getRouter from './AppRouter';
import './i18n/i18n';
import reportWebVitals from './reportWebVitals';
import store, { AppDispatch } from './store';

import '@style/index.scss';

dayjs.extend(duration);

// 閱讀官方文件以了解為什麼這樣做：https://redux.js.org/faq/code-structure#how-can-i-use-the-redux-store-in-non-component-files
injectStore(store);

setPrefixForLocalStorage();

if (localStorage.getItem('DEBUG_LOG') !== 'true') {
  console.log = () => {};
}

function AntdConfigProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const language = useSelector(
    (state: IRootState) => state.preferencesSlice.language,
  );
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(setLanguage(localStorage.getItem('language') || 'jaJP'));
  }, []);

  useEffect(() => {
    dayjs.locale(LanguageMap[language as LanguageKey].dayjs);
    i18n.changeLanguage(LanguageMap[language as LanguageKey].i18n);
  }, [language]);

  return (
    <ConfigProvider
      autoInsertSpaceInButton={false}
      theme={{
        token: { ...SeedToken, ...MapToken, ...AliasToken, ...CustomToken },
        components: Component,
        cssVar: true,
      }}
      locale={LanguageMap[language as LanguageKey].antd}
    >
      {children}
    </ConfigProvider>
  );
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

root.render(
  <StyleProvider transformers={[px2remTransformer()]}>
    <ReduxProvider store={store}>
      <AntdConfigProvider>
        <App
          message={{
            prefixCls: 'custom-message',
          }}
        >
          <RouterProvider router={getRouter()} />
        </App>
      </AntdConfigProvider>
    </ReduxProvider>
  </StyleProvider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
